import { Code, Redo, Undo } from '@mui/icons-material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import TableViewIcon from '@mui/icons-material/TableView';
import { Chip, Divider, Grid, Menu, MenuItem, Select, Stack, styled } from '@mui/material';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useState } from 'react';

import { WsListingLoader } from '../../components/WsListingLoader';
import { WsTypography } from '../../components/WsTypography';
import WsColors from '../../WsColors';
import { Item } from '../Item';

import { CustomTable } from './components/customTable';
import { CustomTableCell } from './components/customTableCell';
import { CustomTableHeader } from './components/customTableHeader';

const IconStyles = {
  cursor: 'pointer',
  ':hover': {
    backgroundColor: WsColors.White,
  },
};

export const BoldIcon = styled(FormatBoldIcon)(() => IconStyles);

export const ColorTextIcon = styled(FormatColorTextIcon)(() => IconStyles);

export const ColorHighlightIcon = styled(BorderColorIcon)(() => IconStyles);

export const ItalicIcon = styled(FormatItalicIcon)(() => IconStyles);

export const LeftAlignIcon = styled(FormatAlignLeftIcon)(() => IconStyles);

export const CenterAlignIcon = styled(FormatAlignCenterIcon)(() => IconStyles);

export const RightAlignIcon = styled(FormatAlignRightIcon)(() => IconStyles);

export const UndoIcon = styled(Undo)(() => IconStyles);

export const RedoIcon = styled(Redo)(() => IconStyles);

export const CodeIcon = styled(Code)(() => IconStyles);

export const TableView = styled(TableViewIcon)(() => IconStyles);

export const TableOptions = styled(PivotTableChartIcon)(() => IconStyles);

export const BulletPointsIcon = styled(FormatListBulletedIcon)(() => IconStyles);

export const EditorToolbar = styled(Item)(({ theme }) => ({
  backgroundColor: WsColors.TertiaryShades100,
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(0),
}));

export const CustomEditorContent = styled(EditorContent)(({ theme }) => ({
  minHeight: '100%',
  height: 'fit-content',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  '.ProseMirror:focus': {
    border: 'none',
    outline: 'none',
  },
}));

const filterHref = (href: string) => {
  const array: string[] = [
    '{{candidate.name}}',
    '{{candidate.email}}',
    '{{candidate.phoneNumber}}',
    '{{candidate.source}}',
    '{{candidate.currentLocation.city}}',
    '{{candidate.currentLocation.state}}',
    '{{candidate.currentLocation.country}}',
    '{{candidate.totalYearsExperience}}',
    '{{candidate.relevantYearsExperience}}',
    '{{candidate.previousJobTitles}}',
    '{{candidate.previousEmployers}}',
    '{{candidate.currentJobTitle}}',
    '{{candidate.currentJobType}}',
    '{{candidate.expectedSalaryCurrency}}',
    '{{candidate.expectedBaseSalary}}',
    '{{candidate.expectedBaseSalaryTimeSpan | splitByUpperCase}}',
    '{{candidate.jobPreferencePermanent}}',
    '{{candidate.jobPreferenceContract}}',
    '{{candidate.currentSalaryCurrency}}',
    '{{candidate.currentSalary}}',
    '{{candidate.currentSalaryTimeSpan | splitByUpperCase}}',
    '{{candidate.currentEmployer}}',
    '{{candidate.otherCurrentAllowances}}',
    '{{candidate.otherExpectedAllowances}}',
    '{{candidate.reasonForNewJob}}',
    '{{candidate.relocationPreference | splitByUpperCase}}',
    "{{candidate.willingToTravel == null ? 'N/A' : (candidate.willingToTravel ? 'Yes' : 'No')}}",
    '{{candidate.familyWillingToRelocate}}',
    '{{candidate.interviewingWithOthers}}',
    '{{candidate.messengers}}',
    '{{candidate.skills}}',
    '{{candidate.assessment}}',
    '{{candidate.workAuthorization}}',
    '{{job.title}}',
    '{{job.jobCode}}',
    '{{job.clientJobCode}}',
    '{{job.jobType}}',
    '{{job.description}}',
    '{{job.tags}}',
    '{{job.skills}}',
    '{{job.companyName}}',
    '{{job.salaryCurrency}} {{job.minimumSalary}} - {{job.salaryCurrency}} {{job.maximumSalary}} {{job.salaryTimeSpan | splitByUpperCase}}',
    '[{{job.locations_city_and_state}}]',
    '[{{job.locations_city_and_country}}]',
    '{{job.isRemote}}',
    '{{job.jobUrl}}',
    '{{recruiter.firstName}} {{recruiter.lastName}}',
    '{{recruiter.email}}',
    '{{recruiter.phoneNumber}}',
    '{{candidate.linkedInURL}}',
    '{{candidate.education}}',
    '{{candidate.reasonForNewJob}}',
    '{{candidate.workspaceName}}',
  ];
  return array.indexOf(href) != -1;
};
const TemplateChip = styled(Chip)(() => ({
  borderRadius: 4,
  backgroundColor: WsColors.TheBlues100,
  border: '1px solid ' + WsColors.TheBlues500,
}));

export interface IEditorProps {
  editor: Editor | null;
  toolbar?: boolean;
  resizable?: boolean;
  templateName?: string;
  editorHeight?: number;
  handleChipClick?: () => void;
  handleChipDelete?: () => void;
  disable?: boolean;
  isLoaded?: boolean;
}
export const useWsEditor = (
  content: string,
  onUpdateHtml?: (args: string | null) => void,
  onUpdateText?: (args: string | null) => void,
  onFocus?: () => void,
  onBlur?: () => void
) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'table'],
      }),
      CustomTable,
      TableRow,
      CustomTableHeader,
      CustomTableCell,
      Link.configure({
        protocols: ['ftp', 'mailto'],
        validate: (href) => filterHref(href),
      }),
      Highlight.configure({ multicolor: true }),
      Color,
      TextStyle,
    ],
    content: content,
    onUpdate: ({ editor }) => {
      if (onUpdateHtml) {
        onUpdateHtml(editor.getHTML());
      }
      if (onUpdateText) {
        onUpdateText(editor.getText());
      }
    },
    onFocus: () => onFocus && onFocus(),
    onBlur: () => onBlur && onBlur(),
  });
  return editor;
};
const WsEditor = ({
  editor,
  toolbar = true,
  resizable = false,
  templateName,
  editorHeight = 400,
  handleChipClick,
  handleChipDelete,
  disable = false,
  isLoaded = true,
}: IEditorProps) => {
  editor?.setEditable(!disable);
  const [tableOptionsAnchor, setTablePopupAnchor] = useState<HTMLElement | null>(null);
  const openTableOptions = Boolean(tableOptionsAnchor);

  const handleTableOptionsClick = (event: any) => {
    setTablePopupAnchor(event.currentTarget);
  };

  const closeTablePopup = () => {
    setTablePopupAnchor(null);
  };

  const inputStyles = {
    width: '20px',
    marginLeft: '-1px',
    margginTop: '5px',
    marginBottom: '-5px',
  };

  const textFieldStyle = (
    display: string,
    marginLeft: string,
    marginBottom: string,
    marginTop: string,
    width: string,
    height: string,
    opacity: string
  ) => {
    return {
      display: display,
      marginLeft: marginLeft,
      marginBottom: marginBottom,
      marginTop: marginTop,
      width: width,
      height: height,
      opacity: opacity,
    };
  };

  return (
    <>
      {toolbar && (
        <EditorToolbar>
          {!disable && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={2}>
                <UndoIcon onClick={() => editor?.chain().focus().undo().run()} />
                <RedoIcon onClick={() => editor?.chain().focus().redo().run()} />
                <Divider orientation="vertical" flexItem />
                <BoldIcon onClick={() => editor?.chain().focus().toggleBold().run()} />
                <ItalicIcon onClick={() => editor?.chain().focus().toggleItalic().run()} />
                <BulletPointsIcon
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                />
                <Divider orientation="vertical" flexItem />
                <LeftAlignIcon onClick={() => editor?.chain().focus().setTextAlign('left').run()} />
                <CenterAlignIcon
                  onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                />
                <RightAlignIcon
                  onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                />
                <Divider orientation="vertical" flexItem />
                <TableView onClick={() => editor?.chain().focus().insertTable().run()} />
                <TableOptions onMouseEnter={handleTableOptionsClick} />
                <Menu
                  anchorEl={tableOptionsAnchor}
                  id="Table Options"
                  open={openTableOptions}
                  onClose={closeTablePopup}
                  onClick={closeTablePopup}
                  onMouseLeave={closeTablePopup}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={() => editor?.chain().focus().addColumnBefore().run()}>
                    Add Column Before
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().addColumnAfter().run()}>
                    Add Column After
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().deleteColumn().run()}>
                    Delete Column
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().addRowBefore().run()}>
                    Add Row Before
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().addRowAfter().run()}>
                    Add Row After
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().deleteRow().run()}>
                    Delete Row
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().mergeCells().run()}>
                    Merge Cells
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().splitCell().run()}>
                    Split Current Cell
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().fixTables().run()}>
                    Fix Table
                  </MenuItem>
                  <MenuItem onClick={() => editor?.chain().focus().deleteTable().run()}>
                    Delete Table
                  </MenuItem>
                </Menu>
                <CodeIcon onClick={() => editor?.chain().focus().setCode().run()} />
                <Select
                  defaultValue={0}
                  size="small"
                  onChange={(ev) =>
                    ev.target.value !== 0
                      ? editor
                          ?.chain()
                          .focus()
                          .toggleHeading({ level: ev.target.value as 1 | 2 | 3 | 4 | 5 | 6 })
                          .run()
                      : editor?.chain().focus().setParagraph().run()
                  }
                >
                  <MenuItem value={0}>Paragraph</MenuItem>
                  <MenuItem value={1}>Heading 1</MenuItem>
                  <MenuItem value={2}>Heading 2</MenuItem>
                  <MenuItem value={3}>Heading 3</MenuItem>
                  <MenuItem value={4}>Heading 4</MenuItem>
                  <MenuItem value={5}>Heading 5</MenuItem>
                  <MenuItem value={6}>Heading 6</MenuItem>
                </Select>
                <Divider orientation="vertical" flexItem />
                <div>
                  <label htmlFor="colorPicker" title="Text color">
                    <i>
                      <ColorTextIcon style={inputStyles} />
                    </i>
                  </label>
                  <input
                    type="color"
                    id="colorPicker"
                    style={textFieldStyle('block', '10px', '10px', '-10px', '0px', '0px', '0')}
                    onInput={(event) =>
                      editor
                        ?.chain()
                        .focus()
                        .setColor((event.target as HTMLInputElement).value)
                        .run()
                    }
                  />
                </div>

                <div>
                  <label htmlFor="highlightColor" className="color-icon" title="Highlight color">
                    <i>
                      <ColorHighlightIcon style={inputStyles} />
                    </i>
                  </label>
                  <input
                    type="color"
                    id="highlightColor"
                    style={textFieldStyle('block', '10px', '10px', '-10px', '0px', '0px', '0')}
                    onInput={(event) =>
                      editor
                        ?.chain()
                        .focus()
                        .toggleHighlight({ color: (event.target as HTMLInputElement).value })
                        .run()
                    }
                  />
                </div>
              </Stack>
              {templateName && (
                <TemplateChip
                  label={templateName.substring(0, 50) + '...'}
                  clickable
                  onClick={handleChipClick}
                  onDelete={handleChipDelete}
                />
              )}
            </Stack>
          )}
        </EditorToolbar>
      )}
      {!isLoaded ? (
        <WsListingLoader>
          <WsTypography bold>Loading Content...</WsTypography>
        </WsListingLoader>
      ) : resizable ? (
        <Grid
          item
          xs={12}
          sx={{ height: editorHeight, width: '100%', overflow: 'auto' }}
          onClick={() => editor?.chain().focus()}
          onKeyDown={() => editor?.chain().focus()}
        >
          <CustomEditorContent editor={editor} disabled={disable} />
        </Grid>
      ) : (
        <CustomEditorContent editor={editor} disabled={disable} />
      )}
    </>
  );
};

export default WsEditor;
